import { CHAR_SCALE } from './character.js';

/**
 * Speech bubbles drawn in world space above the talking character: rounded
 * box + tail, word-wrapped to 220 px, accent-tinted border, ~3.5 s dwell with
 * a gentle pop-in. Replaces the old feed text for banter.
 */

interface Bubble {
  alive: boolean;
  entityId: number;
  text: string;
  accent: string;
  born: number; // performance.now() ms
  dur: number;
}

const ACCENT_EDGE: Record<string, string> = {
  en: '#7ec8ff',
  es: '#ffd76a',
  ru: '#ff8db0',
};

export class Bubbles {
  private readonly pool: Bubble[] = [];

  constructor(private readonly capacity = 6) {}

  show(entityId: number, text: string, accent = 'en', now = performance.now()): void {
    // One bubble per character: replacing beats stacking.
    let slot = this.pool.find((b) => b.alive && b.entityId === entityId);
    if (!slot) {
      slot = this.pool.find((b) => !b.alive);
      if (!slot && this.pool.length < this.capacity) {
        slot = { alive: false, entityId: 0, text: '', accent: 'en', born: 0, dur: 0 };
        this.pool.push(slot);
      }
      if (!slot) {
        // Full: recycle the oldest.
        slot = this.pool.reduce((a, b) => (a.born <= b.born ? a : b));
      }
    }
    slot.alive = true;
    slot.entityId = entityId;
    slot.text = text;
    slot.accent = accent;
    slot.born = now;
    // Long lines need longer to read — +700 ms per extra ~60 chars.
    slot.dur = 3200 + Math.max(0, Math.ceil(text.length / 60) - 1) * 700;
  }

  clear(): void {
    for (const b of this.pool) b.alive = false;
  }

  /** Position lookup: `pos(entityId)` → world feet point, or null if gone. */
  draw(ctx: CanvasRenderingContext2D, pos: (id: number) => { x: number; y: number } | null): void {
    const now = performance.now();
    for (const b of this.pool) {
      if (!b.alive) continue;
      const age = now - b.born;
      if (age > b.dur) {
        b.alive = false;
        continue;
      }
      const anchor = b.entityId === -1 ? null : pos(b.entityId);
      if (!anchor) {
        b.alive = false; // speaker died/left
        continue;
      }

      // Pop-in: scale 0.7 → 1 with a small overshoot in 140 ms; fade tail-end.
      const popT = Math.min(1, age / 140);
      const pop = 0.7 + 0.3 * popT + Math.sin(popT * Math.PI) * 0.12;
      const lifeLeft = b.dur - age;
      const alpha = lifeLeft < 400 ? lifeLeft / 400 : 1;

      const cx = anchor.x;
      const baseY = anchor.y - 58 * CHAR_SCALE; // sits above head/HP/name stack
      ctx.save();
      ctx.globalAlpha = alpha;
      ctx.translate(cx, baseY);
      ctx.scale(pop, pop);

      // Wrap to ≤220 px.
      ctx.font = '12px system-ui';
      const words = b.text.split(/\s+/);
      const lines: string[] = [];
      let line = '';
      for (const w of words) {
        const probe = line ? `${line} ${w}` : w;
        if (ctx.measureText(probe).width > 220 && line) {
          lines.push(line);
          line = w;
        } else {
          line = probe;
        }
      }
      if (line) lines.push(line);

      const lineH = 15;
      const padX = 10;
      const padY = 7;
      let maxW = 0;
      for (const l of lines) maxW = Math.max(maxW, ctx.measureText(l).width);
      const w = maxW + padX * 2;
      const h = lines.length * lineH + padY * 2;
      const left = -w / 2;
      const top = -h - 10; // tail takes the last ~10 px

      // Bubble body.
      ctx.fillStyle = 'rgba(12,16,24,0.88)';
      roundRect(ctx, left, top, w, h, 9);
      ctx.fill();
      ctx.strokeStyle = ACCENT_EDGE[b.accent] ?? '#7ec8ff';
      ctx.lineWidth = 1.6;
      roundRect(ctx, left, top, w, h, 9);
      ctx.stroke();

      // Tail down to the speaker.
      ctx.fillStyle = 'rgba(12,16,24,0.88)';
      ctx.beginPath();
      ctx.moveTo(-5, top + h - 1);
      ctx.lineTo(5, top + h - 1);
      ctx.lineTo(0, top + h + 8);
      ctx.closePath();
      ctx.fill();

      // Text.
      ctx.fillStyle = '#fff';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';
      for (let i = 0; i < lines.length; i++) {
        ctx.fillText(lines[i], 0, top + padY + i * lineH);
      }
      ctx.restore();
    }
    ctx.globalAlpha = 1;
  }
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}
